import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  Add as AddIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import PageHeader from '../../base/PageHeader';
import { BaseButton, BaseTextField } from '../../base';
import BaseModal from '../../base/BaseModal';
import { BaseCheckbox } from '../../base/BaseCheckbox';
import CategoryService from '../../../../services/CategoryService';
import { MediaFile, BreadcrumbItem } from '../../../../types/admin.types';
import MediaService from '../../../../services/MediaService';

interface Category {
  _id?: string;
  id?: string;
  name: string;
  slug?: string;
  icon?: string;
  color?: string;
  description?: string;
}

interface EditingMedia extends MediaFile {
  originalTags?: string[];
}

const COLORS = {
  ACCENT: '#005e5d',
  ACCENT_LIGHT: '#005e5d14',
  SURFACE_SECONDARY: '#F2F3F5',
  BORDER: '#E6E7EB',
  TEXT_PRIMARY: '#333333',
  TEXT_SECONDARY: '#666666',
  TEXT_TERTIARY: '#999999',
};

const FONT = '"Manrope", sans-serif';

const headerCellSx = {
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: '0.75rem',
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  color: COLORS.TEXT_SECONDARY,
  backgroundColor: COLORS.SURFACE_SECONDARY,
  borderBottom: `1px solid ${COLORS.BORDER}`,
  py: 1.5,
  px: 2,
};

const bodyCellSx = {
  fontFamily: FONT,
  fontSize: '0.875rem',
  color: COLORS.TEXT_PRIMARY,
  borderBottom: `1px solid ${COLORS.BORDER}`,
  py: 1.25,
  px: 2,
};

/**
 * Media Management Page with Categories
 * Left sidebar shows categories, right section shows media files
 */
export const MediaManagementPage: React.FC = () => {
  // ============================================
  // STATE
  // ============================================
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [mediaList, setMediaList] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTags, setUploadTags] = useState<string[]>([]);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadIsPublic, setUploadIsPublic] = useState(false);
  const [uploadCategoryId, setUploadCategoryId] = useState<string>('');
  const [tagInput, setTagInput] = useState('');

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMedia, setEditingMedia] = useState<EditingMedia | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editTagInput, setEditTagInput] = useState('');
  const [editIsPublic, setEditIsPublic] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState<string>('');

  // Info dialog state
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoMedia, setInfoMedia] = useState<MediaFile | null>(null);

  // Category dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('📁');
  const [newCategoryColor, setNewCategoryColor] = useState('#61AFFE');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Dashboard', path: '/admin' },
    { label: 'Media Management', path: '/admin/media' },
  ];

  // ============================================
  // LOAD DATA
  // ============================================
  const loadCategories = async () => {
    try {
      const result = await CategoryService.getAllCategories();
      setCategories(result);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError('Failed to load categories');
    }
  };

  const loadMediaList = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await MediaService.listMedia(page, limit, undefined, selectedCategoryId || '');
      setMediaList(response.docs || []);
      setTotal(response.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [page, limit, selectedCategoryId]);

  // ============================================
  // LIFECYCLE
  // ============================================
  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (categories.length > 0 && selectedCategoryId === null) {
      setSelectedCategoryId(categories[0]._id || categories[0].id || '');
    }
  }, [categories, selectedCategoryId]);

  useEffect(() => {
    loadMediaList();
  }, [loadMediaList]);

  // ============================================
  // CATEGORY MANAGEMENT
  // ============================================
  const resetCategoryDialog = () => {
    setNewCategoryName('');
    setNewCategoryIcon('📁');
    setNewCategoryColor('#61AFFE');
    setNewCategoryDescription('');
    setEditingCategoryId(null);
  };

  const handleOpenCategoryDialog = (category?: Category) => {
    if (category) {
      setEditingCategoryId(category._id || category.id || '');
      setNewCategoryName(category.name);
      setNewCategoryIcon(category.icon || '📁');
      setNewCategoryColor(category.color || '#61AFFE');
      setNewCategoryDescription(category.description || '');
    } else {
      resetCategoryDialog();
    }
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!newCategoryName.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      setLoading(true);
      if (editingCategoryId) {
        // Update existing category
        await CategoryService.updateCategory(editingCategoryId, {
          name: newCategoryName,
          icon: newCategoryIcon,
          color: newCategoryColor,
          description: newCategoryDescription,
        });
        setSuccess('Category updated successfully');
      } else {
        // Create new category
        await CategoryService.createCategory({
          name: newCategoryName,
          icon: newCategoryIcon,
          color: newCategoryColor,
          description: newCategoryDescription,
        });
        setSuccess('Category created successfully');
      }
      setCategoryDialogOpen(false);
      resetCategoryDialog();
      await loadCategories();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      setLoading(true);
      await CategoryService.deleteCategory(categoryId);
      setSuccess('Category deleted successfully');
      await loadCategories();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // UPLOAD
  // ============================================
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const handleAddTag = (tag: string) => {
    if (tag.trim() && !uploadTags.includes(tag.trim())) {
      setUploadTags([...uploadTags, tag.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (index: number) => {
    setUploadTags(uploadTags.filter((_, i) => i !== index));
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile) {
      setError('Please select a file');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      await MediaService.uploadFile(
        uploadFile,
        uploadIsPublic,
        uploadTags,
        uploadDescription,
        uploadCategoryId || selectedCategoryId || ''
      );
      setSuccess('File uploaded successfully');

      // Reset form
      setUploadFile(null);
      setUploadTags([]);
      setUploadDescription('');
      setUploadIsPublic(false);
      setUploadCategoryId('');
      setTagInput('');
      setUploadDialogOpen(false);

      // Reload list
      await loadMediaList();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // ============================================
  // EDIT
  // ============================================
  const handleOpenEditDialog = (media: MediaFile) => {
    setEditingMedia(media);
    setEditDescription(media.description || '');
    setEditTags(media.tags || []);
    setEditIsPublic(media.isPublic);
    setEditCategoryId(media.categoryId || '');
    setEditDialogOpen(true);
  };

  const handleAddEditTag = (tag: string) => {
    if (tag.trim() && !editTags.includes(tag.trim())) {
      setEditTags([...editTags, tag.trim()]);
      setEditTagInput('');
    }
  };

  const handleRemoveEditTag = (index: number) => {
    setEditTags(editTags.filter((_, i) => i !== index));
  };

  const handleEditSubmit = async () => {
    if (!editingMedia) return;

    try {
      setLoading(true);
      setError(null);

      await MediaService.updateMedia(editingMedia._id || editingMedia.id || '', {
        description: editDescription,
        tags: editTags,
        isPublic: editIsPublic,
        categoryId: editCategoryId,
      } as any);

      setSuccess('Media updated successfully');
      setEditDialogOpen(false);
      await loadMediaList();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update media');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // DELETE
  // ============================================
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      setLoading(true);
      setError(null);

      await MediaService.deleteMedia(id);
      setSuccess('File deleted successfully');
      await loadMediaList();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete file');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // INFO
  // ============================================
  const handleOpenInfoDialog = (media: MediaFile) => {
    setInfoMedia(media);
    setInfoDialogOpen(true);
  };

  // ============================================
  // DOWNLOAD
  // ============================================
  const handleDownload = async (media: MediaFile) => {
    try {
      // Use download endpoint with filepath if available, otherwise fall back to media.url
      const downloadUrl = media.filepath
        ? MediaService.getDownloadUrl(media.filepath)
        : media.url || MediaService.getDownloadUrl(media.filename);

      const token = localStorage.getItem('accessToken') || '';
      const response = await fetch(downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = media.originalName || media.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    }
  };

  // ============================================
  // FILTERING
  // ============================================
  const filteredMedia = mediaList.filter(
    (media) =>
      media.originalName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      media.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      media.tags?.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const selectedCategory = categories.find(c => (c._id || c.id) === selectedCategoryId);

  // ============================================
  // RENDER
  // ============================================
  return (
    <Box>
      <PageHeader
        title="Media Management"
        subtitle="Organize, upload, and manage your media files"
        breadcrumbs={breadcrumbs}
      />

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Main Layout: Sidebar + Content */}
      <Box sx={{ display: 'flex', gap: 2, minHeight: 'calc(100vh - 300px)' }}>
        {/* LEFT SIDEBAR - Categories */}
        <Paper
          elevation={0}
          sx={{
            width: 280,
            p: 2,
            borderRadius: '14px',
            border: `1px solid ${COLORS.BORDER}`,
            flexShrink: 0,
            overflowY: 'auto',
          }}
        >
          {/* Sidebar Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.TEXT_PRIMARY }}>
              Categories
            </Typography>
            <Tooltip title="New Category">
              <IconButton
                size="small"
                onClick={() => setCategoryDialogOpen(true)}
                sx={{ color: COLORS.ACCENT }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {/* Category List */}
          <List disablePadding>
            {categories.map((category) => (
              <Box key={category._id || category.id}>
                <ListItem
                  selected={(category._id || category.id) === selectedCategoryId}
                  onClick={() => {
                    setSelectedCategoryId(category._id || category.id || '');
                    setPage(1);
                  }}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    cursor: 'pointer',
                    backgroundColor:
                      (category._id || category.id) === selectedCategoryId
                        ? `${COLORS.ACCENT}15`
                        : 'transparent',
                    '&:hover': {
                      backgroundColor: `${COLORS.ACCENT}10`,
                    },
                    transition: 'all 0.2s',
                    pr: 0.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: 20,
                    }}
                  >
                    {category.icon || '📁'}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: (category._id || category.id) === selectedCategoryId ? 700 : 600,
                          color: COLORS.TEXT_PRIMARY,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {category.name}
                      </Typography>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenCategoryDialog(category);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCategory(category._id || category.id || '');
                        }}
                        sx={{ p: 0.5, color: 'error.main' }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              </Box>
            ))}
          </List>
        </Paper>

        {/* RIGHT CONTENT - Media Files */}
        <Box sx={{ flex: 1 }}>
          {/* Toolbar */}
          <Paper
            sx={{
              p: 2,
              mb: 2,
              borderRadius: '14px',
              border: `1px solid ${COLORS.BORDER}`,
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
              <SearchIcon sx={{ color: COLORS.TEXT_TERTIARY }} />
              <BaseTextField
                placeholder="Search files..."
                fullWidth
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm((e.target as HTMLInputElement).value)}
              />
            </Box>
            <BaseButton
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
              sx={{
                backgroundColor: COLORS.ACCENT,
                '&:hover': {
                  backgroundColor: COLORS.ACCENT,
                  opacity: 0.9,
                },
                whiteSpace: 'nowrap',
              }}
            >
              Upload
            </BaseButton>
          </Paper>

          {/* Category Info */}
          {selectedCategory&& selectedCategory.description && (
            <Paper
              sx={{
                p: 2,
                mb: 2,
                borderRadius: '14px',
                border: `1px solid ${COLORS.BORDER}`,
                backgroundColor: `${selectedCategory.color || '#61AFFE'}08`,
              }}
            >
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 32 }}>
                    {selectedCategory.icon || '📁'}
                  </Typography>
                  <Box>

                    <Typography sx={{ fontSize: 12, color: COLORS.TEXT_TERTIARY }}>
                      {selectedCategory.description}
                    </Typography>

                  </Box>
                </Box>
            </Paper>
          )}

          {/* Media Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={32} sx={{ color: COLORS.ACCENT }} />
            </Box>
          ) : filteredMedia.length === 0 ? (
            <Box sx={{ border: `2px dashed ${COLORS.BORDER}`, borderRadius: '12px', py: 8, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: '2rem', mb: 1 }}>📭</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: '0.9375rem', color: COLORS.TEXT_TERTIARY }}>
                No media files in this category yet
              </Typography>
            </Box>
          ) : (
            <Paper elevation={0} sx={{ borderRadius: '10px', border: `1px solid ${COLORS.BORDER}`, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellSx, width: 56 }}>Preview</TableCell>
                      <TableCell sx={headerCellSx}>Name</TableCell>
                      <TableCell sx={headerCellSx}>Type</TableCell>
                      <TableCell sx={headerCellSx}>Size</TableCell>
                      <TableCell sx={headerCellSx}>Tags</TableCell>
                      <TableCell sx={headerCellSx}>Status</TableCell>
                      <TableCell sx={headerCellSx}>Uploaded</TableCell>
                      <TableCell sx={{ ...headerCellSx, textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMedia.map((media) => (
                      <TableRow
                        key={media._id || media.id}
                        sx={{ '&:hover': { backgroundColor: COLORS.SURFACE_SECONDARY }, '&:last-child td': { borderBottom: 'none' } }}
                      >
                        {/* Preview */}
                        <TableCell sx={{ ...bodyCellSx, px: 1.5 }}>
                          {media.mimeType?.startsWith('image/') && media.url ? (
                            <Box
                              component="img"
                              src={media.url}
                              alt={media.originalName}
                              sx={{ width: 40, height: 40, borderRadius: '6px', objectFit: 'cover', border: `1px solid ${COLORS.BORDER}`, display: 'block' }}
                            />
                          ) : (
                            <Box sx={{ width: 40, height: 40, borderRadius: '6px', backgroundColor: COLORS.SURFACE_SECONDARY, border: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <FileIcon sx={{ fontSize: 20, color: COLORS.TEXT_TERTIARY }} />
                            </Box>
                          )}
                        </TableCell>

                        {/* Name */}
                        <TableCell sx={{ ...bodyCellSx, maxWidth: 220 }}>
                          <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', fontWeight: 600, color: COLORS.TEXT_PRIMARY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={media.originalName}>
                            {media.originalName}
                          </Typography>
                          {media.description && (
                            <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: COLORS.TEXT_TERTIARY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {media.description}
                            </Typography>
                          )}
                        </TableCell>

                        {/* MIME type */}
                        <TableCell sx={{ ...bodyCellSx, color: COLORS.TEXT_TERTIARY, fontSize: '0.8125rem' }}>
                          {media.mimeType?.split('/')[1]?.toUpperCase() ?? media.mimeType}
                        </TableCell>

                        {/* Size */}
                        <TableCell sx={{ ...bodyCellSx, color: COLORS.TEXT_SECONDARY, whiteSpace: 'nowrap' }}>
                          {MediaService.formatFileSize(media.fileSize)}
                        </TableCell>

                        {/* Tags */}
                        <TableCell sx={bodyCellSx}>
                          {media.tags && media.tags.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {media.tags.slice(0, 3).map((tag, i) => (
                                <Box key={i} sx={{ px: 1, py: 0.25, borderRadius: '5px', backgroundColor: COLORS.SURFACE_SECONDARY, border: `1px solid ${COLORS.BORDER}` }}>
                                  <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: COLORS.TEXT_SECONDARY }}>{tag}</Typography>
                                </Box>
                              ))}
                              {media.tags.length > 3 && (
                                <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: COLORS.TEXT_TERTIARY, alignSelf: 'center' }}>+{media.tags.length - 3}</Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography sx={{ fontFamily: FONT, fontSize: '0.8125rem', color: COLORS.TEXT_TERTIARY }}>—</Typography>
                          )}
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={bodyCellSx}>
                          {media.isPublic ? (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.25, py: 0.4, borderRadius: '6px', backgroundColor: '#E8F5E9' }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#2E7D32' }} />
                              <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', fontWeight: 700, color: '#2E7D32' }}>Public</Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.25, py: 0.4, borderRadius: '6px', backgroundColor: COLORS.SURFACE_SECONDARY }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: COLORS.TEXT_TERTIARY }} />
                              <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', fontWeight: 700, color: COLORS.TEXT_TERTIARY }}>Private</Typography>
                            </Box>
                          )}
                        </TableCell>

                        {/* Uploaded date */}
                        <TableCell sx={{ ...bodyCellSx, color: COLORS.TEXT_TERTIARY, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                          {media.createdAt ? new Date(media.createdAt).toLocaleDateString() : '—'}
                        </TableCell>

                        {/* Actions */}
                        <TableCell sx={{ ...bodyCellSx, textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <Tooltip title="Info">
                            <IconButton size="small" onClick={() => handleOpenInfoDialog(media)} sx={{ color: COLORS.TEXT_SECONDARY, borderRadius: '6px', '&:hover': { backgroundColor: COLORS.SURFACE_SECONDARY } }}>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(media)}
                              sx={{ color: '#1565C0', borderRadius: '6px', '&:hover': { backgroundColor: '#E3F2FD' }, mx: 0.25 }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenEditDialog(media)} sx={{ color: COLORS.ACCENT, borderRadius: '6px', '&:hover': { backgroundColor: COLORS.ACCENT_LIGHT }, mx: 0.25 }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(media._id || media.id || '')} sx={{ color: '#D32F2F', borderRadius: '6px', '&:hover': { backgroundColor: 'rgba(211,47,47,0.08)' } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page - 1}
                rowsPerPage={limit}
                onPageChange={(_, newPage) => setPage(newPage + 1)}
                onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(1); }}
                rowsPerPageOptions={[12, 25, 50]}
                sx={{
                  borderTop: `1px solid ${COLORS.BORDER}`,
                  fontFamily: FONT,
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
                    fontFamily: FONT, fontSize: '0.8125rem', color: COLORS.TEXT_SECONDARY,
                  },
                }}
              />
            </Paper>
          )}
        </Box>
      </Box>

      {/* DIALOGS */}

      {/* Upload Dialog */}
      <BaseModal
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        title="Upload File"
        maxWidth="sm"
        submitButtonText="Upload"
        isLoading={uploading}
        onSubmit={handleUploadSubmit}
      >
        {/* Category Selection */}
        <BaseTextField
          select
          label="Category"
          fullWidth
          value={uploadCategoryId || selectedCategoryId || ''}
          onChange={(e) => setUploadCategoryId(e.target.value)}
          margin="normal"
          SelectProps={{
            native: true,
          }}
        >
          {categories.map((cat) => (
            <option key={cat._id || cat.id} value={cat._id || cat.id || ''}>
              {cat.icon} {cat.name}
            </option>
          ))}
        </BaseTextField>

        {/* File Selection */}
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            my: 2,
            textAlign: 'center',
            backgroundColor: '#fafafa',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { backgroundColor: '#f0f0f0' },
          }}
          component="label"
        >
          <input type="file" hidden onChange={handleFileChange} />
          {uploadFile ? (
            <Box>
              <Typography variant="body2" color="primary">
                ✓ {uploadFile.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {MediaService.formatFileSize(uploadFile.size)}
              </Typography>
            </Box>
          ) : (
            <Box>
              <UploadIcon sx={{ fontSize: 32, color: 'action.disabled', mb: 1 }} />
              <Typography variant="body2">Click to select file</Typography>
            </Box>
          )}
        </Box>

        {/* Description */}
        <BaseTextField
          label="Description"
          multiline
          rows={2}
          fullWidth
          value={uploadDescription}
          onChange={(e) => setUploadDescription(e.target.value)}
          margin="normal"
          placeholder="Add a description"
        />

        {/* Tags */}
        <Box sx={{ mt: 2 }}>
          <BaseTextField
            label="Add Tag"
            size="small"
            fullWidth
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddTag(tagInput);
              }
            }}
            placeholder="Type and press Enter"
          />
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {uploadTags.map((tag, index) => (
              <Chip key={index} label={tag} onDelete={() => handleRemoveTag(index)} />
            ))}
          </Box>
        </Box>

        {/* Public Toggle */}
        <Box sx={{ mt: 2 }}>
          <BaseCheckbox
            label="Make file public"
            checked={uploadIsPublic}
            onChange={(checked) => setUploadIsPublic(checked)}
          />
        </Box>
      </BaseModal>

      {/* Category Dialog */}
      <BaseModal
        open={categoryDialogOpen}
        onClose={() => {
          setCategoryDialogOpen(false);
          resetCategoryDialog();
        }}
        title={editingCategoryId ? 'Edit Category' : 'Create New Category'}
        maxWidth="sm"
        submitButtonText={editingCategoryId ? 'Update' : 'Create'}
        isLoading={loading}
        onSubmit={handleSaveCategory}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <BaseTextField
            label="Category Name"
            fullWidth
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="e.g., Screenshots, Logos, etc."
          />

          <BaseTextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={newCategoryDescription}
            onChange={(e) => setNewCategoryDescription(e.target.value)}
            placeholder="Optional description for this category"
          />

          <BaseTextField
            label="Icon (Emoji)"
            fullWidth
            value={newCategoryIcon}
            onChange={(e) => setNewCategoryIcon(e.target.value)}
            placeholder="📁"
            inputProps={{ maxLength: 2 }}
          />

          <BaseTextField
            label="Color"
            type="color"
            fullWidth
            value={newCategoryColor}
            onChange={(e) => setNewCategoryColor(e.target.value)}
            InputProps={{
              sx: { height: 40 },
            }}
          />
        </Box>
      </BaseModal>

      {/* Edit Media Dialog */}
      <BaseModal
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title="Edit Media"
        maxWidth="sm"
        submitButtonText="Save"
        isLoading={loading}
        onSubmit={handleEditSubmit}
      >
        {editingMedia && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* File Info */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: '#fafafa',
                borderRadius: 1,
                border: `1px solid ${COLORS.BORDER}`,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {editingMedia.originalName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {MediaService.formatFileSize(editingMedia.fileSize)} • {editingMedia.mimeType}
              </Typography>
            </Paper>

            {/* Category Selection */}
            <BaseTextField
              select
              label="Category"
              fullWidth
              value={editCategoryId || ''}
              onChange={(e) => setEditCategoryId(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="">No Category</option>
              {categories.map((cat) => (
                <option key={cat._id || cat.id} value={cat._id || cat.id || ''}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </BaseTextField>

            {/* Description */}
            <BaseTextField
              label="Description"
              multiline
              rows={3}
              fullWidth
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Edit description for this file"
            />

            {/* Tags */}
            <Box>
              <BaseTextField
                label="Add Tag"
                size="small"
                fullWidth
                value={editTagInput}
                onChange={(e) => setEditTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddEditTag(editTagInput);
                  }
                }}
                placeholder="Type and press Enter"
              />
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {editTags.map((tag, index) => (
                  <Chip key={index} label={tag} onDelete={() => handleRemoveEditTag(index)} />
                ))}
              </Box>
            </Box>

            {/* Public Toggle */}
            <BaseCheckbox
              label="Make file public"
              checked={editIsPublic}
              onChange={(checked) => setEditIsPublic(checked)}
            />
          </Box>
        )}
      </BaseModal>

      {/* Info Dialog */}
      <BaseModal
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        title="File Information"
        maxWidth="sm"
        showFooter={false}
      >
        {infoMedia && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* File Preview */}
            {infoMedia.mimeType?.startsWith('image/') && infoMedia.url && (
              <Box
                component="img"
                src={infoMedia.url}
                alt={infoMedia.originalName}
                sx={{ width: '100%', borderRadius: 1, maxHeight: 300 }}
              />
            )}

            {/* Filename */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: COLORS.TEXT_SECONDARY }}>
                Filename
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all', mt: 0.5 }}>
                {infoMedia.originalName}
              </Typography>
            </Box>

            {/* File Size */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: COLORS.TEXT_SECONDARY }}>
                File Size
              </Typography>
              <Typography variant="body2">
                {MediaService.formatFileSize(infoMedia.fileSize)}
              </Typography>
            </Box>

            {/* MIME Type */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: COLORS.TEXT_SECONDARY }}>
                MIME Type
              </Typography>
              <Typography variant="body2">{infoMedia.mimeType}</Typography>
            </Box>

            {/* Description */}
            {infoMedia.description && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: COLORS.TEXT_SECONDARY }}>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {infoMedia.description}
                </Typography>
              </Box>
            )}

            {/* Tags */}
            {infoMedia.tags && infoMedia.tags.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: COLORS.TEXT_SECONDARY }}>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {infoMedia.tags.map((tag, idx) => (
                    <Chip key={idx} label={tag} size="small" />
                  ))}
                </Box>
              </Box>
            )}

            {/* Status */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: COLORS.TEXT_SECONDARY }}>
                Status
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={infoMedia.isPublic ? 'Public' : 'Private'}
                  color={infoMedia.isPublic ? 'primary' : 'default'}
                  size="small"
                />
              </Box>
            </Box>

            {/* Uploaded Date */}
            {infoMedia.createdAt && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: COLORS.TEXT_SECONDARY }}>
                  Uploaded
                </Typography>
                <Typography variant="body2">
                  {new Date(infoMedia.createdAt).toLocaleString()}
                </Typography>
              </Box>
            )}

            {/* Download Button */}
            {infoMedia.url && (
              <BaseButton
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => window.open(infoMedia.url, '_blank')}
              >
                Download File
              </BaseButton>
            )}
          </Box>
        )}
      </BaseModal>
    </Box>
  );
};

export default MediaManagementPage;
